import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import '@fontsource/anton/latin-400.css';
import '@fontsource/noto-sans-kr/korean-400.css';
import '@fontsource/noto-sans-kr/korean-900.css';
import { xadmApi } from '../../api/xadm.js';
import { ErrorBox } from '../../components/ui.js';

/**
 * 배송현황 카운터 — 원본 /home/bananapartner/xadm/delivery_status_inframe.php.
 *
 * TV 전광판에 띄우던 큰 숫자 카운터. 원본은 delivery_status.php 가 AJAX 로
 * 안쪽 화면을 반복 교체했지만, 여기서는 화면 자체가 폴링(REFRESH_MS)한다.
 * 공개 키오스크 라우트라 관리자 Layout/AuthProvider 는 App.tsx 에서 제외한다.
 *
 * 원본의 특정 과거 날짜 하드코딩 보정(연휴 이월 물량 등)은 counter.service.ts
 * 주석 참고 - 전부 지난 날짜라 옮기지 않았다.
 */

const REFRESH_MS = 30_000;

/** 원본처럼 자릿수마다 "그 자리까지 값이 있으면 숫자, 없으면 회색 0" 으로 나눈다. */
function Digits({ value, label }: { value: number; label: string }) {
  const v = Math.max(0, Math.trunc(value));
  const text = String(v).padStart(4, '0').slice(-4);
  const thresholds = [999, 99, 9, 0];

  return (
    <div className="counter-digits" role="img" aria-label={`${label} ${v.toLocaleString()}건`}>
      {text.split('').map((d, i) => {
        const significant = v > thresholds[i]!;
        return (
          <span key={i} aria-hidden="true" className={significant ? 'counter-digit' : 'counter-digit counter-digit--zero'}>
            {/* 가로로만 늘린 전광판 자형 - 세로는 그대로라 안쪽 span 에만 scaleX 를 건다. */}
            <span className="counter-digit__glyph">{significant ? d : '0'}</span>
          </span>
        );
      })}
    </div>
  );
}

const twoDigits = (value: number) => String(value).padStart(2, '0');

function clockDate(value: Date) {
  return `${value.getFullYear()}/${twoDigits(value.getMonth() + 1)}/${twoDigits(value.getDate())}`;
}

function clockTime(value: Date) {
  return `${twoDigits(value.getHours())}:${twoDigits(value.getMinutes())}:${twoDigits(value.getSeconds())}`;
}

export function LogisticsCounterPage() {
  const [params] = useSearchParams();
  const start = params.get('start') ?? '';
  const end = params.get('end') ?? '';

  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const counter = useQuery({
    queryKey: ['xadm-logistics-counter', start, end],
    queryFn: () => xadmApi.logisticsCounter(start || undefined, end || undefined),
    refetchInterval: REFRESH_MS,
  });

  // 서버가 센 이월 건수와 상세. 화면에서 숫자를 더하거나 빼지 않는다.
  const carriedOver = counter.data?.carriedOver ?? 0;
  const carryTitle = (counter.data?.carriedOverOrders ?? [])
    .slice(0, 20)
    .map((o) => `[이월] ${o.orderNo || o.invoiceNo} (${o.orderStatusLabel})`)
    .join('\n');

  return (
    <main className="counter-page counter-page--kiosk">
      <div className="counter-display" aria-busy={counter.isFetching && !counter.data}>
        {counter.isError && (
          <div className="counter-kiosk-error">
            <ErrorBox error={counter.error} onRetry={() => counter.refetch()} />
          </div>
        )}

        <ul className="counter-board" aria-label="배송 처리 현황">
          <li className="counter-board__row counter-board__row--total">
            {/* 시계는 별도 헤더 없이 총(TOTAL) 칸 위쪽에 얹는다. */}
            <div className="counter-label counter-label--clock">
              <div className="counter-clock">
                <span>{clockDate(now)}</span>
                <strong>{clockTime(now)}</strong>
              </div>
              <div className="counter-label__name"><small>TOTAL</small><strong>총</strong></div>
            </div>
            <Digits value={counter.data?.withCarryOver?.total ?? 0} label="총 주문" />
          </li>
          <li className="counter-board__row counter-board__row--processed">
            <div className="counter-label"><small>COMPLETE</small><strong>완료</strong></div>
            {/* 이월분을 완료한 건수까지 포함된 값. 원본 processed 는 어제 발행분을
                오늘 처리한 건을 세지 않아서 이 칸이 안 움직였다. */}
            <Digits value={counter.data?.withCarryOver?.processed ?? 0} label="처리 완료" />
          </li>
          <li className="counter-board__row counter-board__row--pending">
            <div className="counter-label">
              <small>INVOICE</small>
              <strong>송장</strong>
              {/* 이월분은 서버가 세어 준 값을 그대로 띄운다 - 화면에서 더하지 않는다. */}
              {carriedOver > 0 && (
                <span className="counter-carry" title={carryTitle}>이월 {carriedOver.toLocaleString()}</span>
              )}
            </div>
            <Digits value={counter.data?.withCarryOver?.notProcessed ?? 0} label="송장 미처리" />
          </li>
        </ul>
      </div>
    </main>
  );
}
